import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request, requestBlob } from '../api/client';

/**
 * Países suportados na criação/edição de mapa avulso. Valor = código ISO
 * (BR/PT/BO…) que o geocoder do serviço de astrologia usa pra filtrar por país.
 * O nome legível aparece no PDF.
 */
const PAISES: ReadonlyArray<{ code: string; nome: string }> = [
  { code: 'AF', nome: 'Afeganistão' },
  { code: 'ZA', nome: 'África do Sul' },
  { code: 'AL', nome: 'Albânia' },
  { code: 'DE', nome: 'Alemanha' },
  { code: 'AD', nome: 'Andorra' },
  { code: 'AO', nome: 'Angola' },
  { code: 'AG', nome: 'Antígua e Barbuda' },
  { code: 'SA', nome: 'Arábia Saudita' },
  { code: 'DZ', nome: 'Argélia' },
  { code: 'AR', nome: 'Argentina' },
  { code: 'AM', nome: 'Armênia' },
  { code: 'AU', nome: 'Austrália' },
  { code: 'AT', nome: 'Áustria' },
  { code: 'AZ', nome: 'Azerbaijão' },
  { code: 'BS', nome: 'Bahamas' },
  { code: 'BD', nome: 'Bangladesh' },
  { code: 'BB', nome: 'Barbados' },
  { code: 'BH', nome: 'Barein' },
  { code: 'BE', nome: 'Bélgica' },
  { code: 'BZ', nome: 'Belize' },
  { code: 'BJ', nome: 'Benin' },
  { code: 'BY', nome: 'Bielorrússia' },
  { code: 'BO', nome: 'Bolívia' },
  { code: 'BA', nome: 'Bósnia e Herzegovina' },
  { code: 'BW', nome: 'Botsuana' },
  { code: 'BR', nome: 'Brasil' },
  { code: 'BN', nome: 'Brunei' },
  { code: 'BG', nome: 'Bulgária' },
  { code: 'BF', nome: 'Burquina Faso' },
  { code: 'BI', nome: 'Burundi' },
  { code: 'BT', nome: 'Butão' },
  { code: 'CV', nome: 'Cabo Verde' },
  { code: 'CM', nome: 'Camarões' },
  { code: 'KH', nome: 'Camboja' },
  { code: 'CA', nome: 'Canadá' },
  { code: 'QA', nome: 'Catar' },
  { code: 'KZ', nome: 'Cazaquistão' },
  { code: 'TD', nome: 'Chade' },
  { code: 'CL', nome: 'Chile' },
  { code: 'CN', nome: 'China' },
  { code: 'CY', nome: 'Chipre' },
  { code: 'VA', nome: 'Cidade do Vaticano' },
  { code: 'CO', nome: 'Colômbia' },
  { code: 'KM', nome: 'Comores' },
  { code: 'CD', nome: 'Congo - Kinshasa' },
  { code: 'KP', nome: 'Coreia do Norte' },
  { code: 'KR', nome: 'Coreia do Sul' },
  { code: 'CI', nome: 'Costa do Marfim' },
  { code: 'CR', nome: 'Costa Rica' },
  { code: 'HR', nome: 'Croácia' },
  { code: 'CU', nome: 'Cuba' },
  { code: 'DK', nome: 'Dinamarca' },
  { code: 'DJ', nome: 'Djibuti' },
  { code: 'DM', nome: 'Dominica' },
  { code: 'EG', nome: 'Egito' },
  { code: 'SV', nome: 'El Salvador' },
  { code: 'AE', nome: 'Emirados Árabes Unidos' },
  { code: 'EC', nome: 'Equador' },
  { code: 'ER', nome: 'Eritreia' },
  { code: 'SK', nome: 'Eslováquia' },
  { code: 'SI', nome: 'Eslovênia' },
  { code: 'ES', nome: 'Espanha' },
  { code: 'SZ', nome: 'Essuatíni' },
  { code: 'US', nome: 'Estados Unidos' },
  { code: 'EE', nome: 'Estônia' },
  { code: 'ET', nome: 'Etiópia' },
  { code: 'FJ', nome: 'Fiji' },
  { code: 'PH', nome: 'Filipinas' },
  { code: 'FI', nome: 'Finlândia' },
  { code: 'FR', nome: 'França' },
  { code: 'GA', nome: 'Gabão' },
  { code: 'GM', nome: 'Gâmbia' },
  { code: 'GH', nome: 'Gana' },
  { code: 'GE', nome: 'Geórgia' },
  { code: 'GD', nome: 'Granada' },
  { code: 'GR', nome: 'Grécia' },
  { code: 'GT', nome: 'Guatemala' },
  { code: 'GY', nome: 'Guiana' },
  { code: 'GN', nome: 'Guiné' },
  { code: 'GQ', nome: 'Guiné Equatorial' },
  { code: 'GW', nome: 'Guiné-Bissau' },
  { code: 'HT', nome: 'Haiti' },
  { code: 'HN', nome: 'Honduras' },
  { code: 'HK', nome: 'Hong Kong, RAE da China' },
  { code: 'HU', nome: 'Hungria' },
  { code: 'YE', nome: 'Iêmen' },
  { code: 'MH', nome: 'Ilhas Marshall' },
  { code: 'SB', nome: 'Ilhas Salomão' },
  { code: 'IN', nome: 'Índia' },
  { code: 'ID', nome: 'Indonésia' },
  { code: 'IR', nome: 'Irã' },
  { code: 'IQ', nome: 'Iraque' },
  { code: 'IE', nome: 'Irlanda' },
  { code: 'IS', nome: 'Islândia' },
  { code: 'IL', nome: 'Israel' },
  { code: 'IT', nome: 'Itália' },
  { code: 'JM', nome: 'Jamaica' },
  { code: 'JP', nome: 'Japão' },
  { code: 'JO', nome: 'Jordânia' },
  { code: 'KW', nome: 'Kuwait' },
  { code: 'LA', nome: 'Laos' },
  { code: 'LS', nome: 'Lesoto' },
  { code: 'LV', nome: 'Letônia' },
  { code: 'LB', nome: 'Líbano' },
  { code: 'LR', nome: 'Libéria' },
  { code: 'LY', nome: 'Líbia' },
  { code: 'LI', nome: 'Liechtenstein' },
  { code: 'LT', nome: 'Lituânia' },
  { code: 'LU', nome: 'Luxemburgo' },
  { code: 'MO', nome: 'Macau, RAE da China' },
  { code: 'MK', nome: 'Macedônia do Norte' },
  { code: 'MG', nome: 'Madagascar' },
  { code: 'MY', nome: 'Malásia' },
  { code: 'MW', nome: 'Malaui' },
  { code: 'MV', nome: 'Maldivas' },
  { code: 'ML', nome: 'Mali' },
  { code: 'MT', nome: 'Malta' },
  { code: 'MA', nome: 'Marrocos' },
  { code: 'MU', nome: 'Maurício' },
  { code: 'MR', nome: 'Mauritânia' },
  { code: 'MX', nome: 'México' },
  { code: 'MM', nome: 'Mianmar (Birmânia)' },
  { code: 'FM', nome: 'Micronésia' },
  { code: 'MZ', nome: 'Moçambique' },
  { code: 'MD', nome: 'Moldávia' },
  { code: 'MC', nome: 'Mônaco' },
  { code: 'MN', nome: 'Mongólia' },
  { code: 'ME', nome: 'Montenegro' },
  { code: 'NA', nome: 'Namíbia' },
  { code: 'NR', nome: 'Nauru' },
  { code: 'NP', nome: 'Nepal' },
  { code: 'NI', nome: 'Nicarágua' },
  { code: 'NE', nome: 'Níger' },
  { code: 'NG', nome: 'Nigéria' },
  { code: 'NO', nome: 'Noruega' },
  { code: 'NZ', nome: 'Nova Zelândia' },
  { code: 'OM', nome: 'Omã' },
  { code: 'NL', nome: 'Países Baixos' },
  { code: 'PW', nome: 'Palau' },
  { code: 'PA', nome: 'Panamá' },
  { code: 'PG', nome: 'Papua-Nova Guiné' },
  { code: 'PK', nome: 'Paquistão' },
  { code: 'PY', nome: 'Paraguai' },
  { code: 'PE', nome: 'Peru' },
  { code: 'PL', nome: 'Polônia' },
  { code: 'PR', nome: 'Porto Rico' },
  { code: 'PT', nome: 'Portugal' },
  { code: 'KE', nome: 'Quênia' },
  { code: 'KG', nome: 'Quirguistão' },
  { code: 'KI', nome: 'Quiribati' },
  { code: 'GB', nome: 'Reino Unido' },
  { code: 'CF', nome: 'República Centro-Africana' },
  { code: 'CG', nome: 'República do Congo' },
  { code: 'DO', nome: 'República Dominicana' },
  { code: 'RO', nome: 'Romênia' },
  { code: 'RW', nome: 'Ruanda' },
  { code: 'RU', nome: 'Rússia' },
  { code: 'WS', nome: 'Samoa' },
  { code: 'SM', nome: 'San Marino' },
  { code: 'LC', nome: 'Santa Lúcia' },
  { code: 'KN', nome: 'São Cristóvão e Névis' },
  { code: 'ST', nome: 'São Tomé e Príncipe' },
  { code: 'VC', nome: 'São Vicente e Granadinas' },
  { code: 'SC', nome: 'Seicheles' },
  { code: 'SN', nome: 'Senegal' },
  { code: 'SL', nome: 'Serra Leoa' },
  { code: 'RS', nome: 'Sérvia' },
  { code: 'SG', nome: 'Singapura' },
  { code: 'SY', nome: 'Síria' },
  { code: 'SO', nome: 'Somália' },
  { code: 'LK', nome: 'Sri Lanka' },
  { code: 'SD', nome: 'Sudão' },
  { code: 'SS', nome: 'Sudão do Sul' },
  { code: 'SE', nome: 'Suécia' },
  { code: 'CH', nome: 'Suíça' },
  { code: 'SR', nome: 'Suriname' },
  { code: 'TJ', nome: 'Tadjiquistão' },
  { code: 'TH', nome: 'Tailândia' },
  { code: 'TW', nome: 'Taiwan' },
  { code: 'TZ', nome: 'Tanzânia' },
  { code: 'CZ', nome: 'Tchéquia' },
  { code: 'PS', nome: 'Territórios palestinos' },
  { code: 'TL', nome: 'Timor-Leste' },
  { code: 'TG', nome: 'Togo' },
  { code: 'TO', nome: 'Tonga' },
  { code: 'TT', nome: 'Trinidad e Tobago' },
  { code: 'TN', nome: 'Tunísia' },
  { code: 'TM', nome: 'Turcomenistão' },
  { code: 'TR', nome: 'Turquia' },
  { code: 'TV', nome: 'Tuvalu' },
  { code: 'UA', nome: 'Ucrânia' },
  { code: 'UG', nome: 'Uganda' },
  { code: 'UY', nome: 'Uruguai' },
  { code: 'UZ', nome: 'Uzbequistão' },
  { code: 'VU', nome: 'Vanuatu' },
  { code: 'VE', nome: 'Venezuela' },
  { code: 'VN', nome: 'Vietnã' },
  { code: 'ZM', nome: 'Zâmbia' },
  { code: 'ZW', nome: 'Zimbábue' },
];

type Origem = 'sara' | 'webapp' | 'avulso';
type Bucket = 'precisa_de_voce' | 'em_andamento' | 'concluido';

interface InboxPdf {
  id: string;
  versao: number;
  status: string;
  temArquivo: boolean;
  errorMessage: string | null;
  deliverAt: string | null;
  deliveredAt: string | null;
  /** Status do email desta versão; null = nunca saiu por email. */
  emailStatus: string | null;
}

/** Item da inbox unificada (GET /admin/mapas/inbox). */
interface MapaInbox {
  id: string;
  origem: Origem;
  bucket: Bucket;
  nome: string | null;
  telefone: string | null;
  cliente: { primeiroNome: string; nomeCompleto: string | null } | null;
  dataNasc: string;
  horaNasc: string | null;
  horaConhecida: boolean;
  cidadeNasc: string;
  ufNasc: string | null;
  paisNasc: string;
  statusCalculo: string;
  ultimoErro: string | null;
  entregaHumanaSolicitadaEm: string | null;
  createdAt: string;
  // Mapa do webapp EN (self-service): email do cliente + quando preencheu.
  webappEmail: string | null;
  preenchidoEm: string | null;
  latestPdf: InboxPdf | null;
  contato: {
    id: string;
    nome: string;
    telefone: string;
    email: string | null;
    canal: string | null;
    janela24hAberta: boolean;
  } | null;
}

interface ContatoBusca {
  id: string;
  primeiroNome: string;
  telefone: string;
}

interface PdfVersion {
  id: string;
  versao: number;
  status: 'gerando' | 'pendente_aprovacao' | 'aprovado' | 'rejeitado' | 'entregue' | 'falhou';
  generatedAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  deliverAt: string | null;
  deliveredAt: string | null;
  feedback: string | null;
  llmCusto: number | null;
  sizeKb: number | null;
  errorMessage: string | null;
  emailEnviadoEm: string | null;
  emailStatus: string | null;
  /** `auto-aprovacao` = saiu sem ninguém ler. Ver a etiqueta abaixo. */
  approvedBy: string | null;
}

const STATUS_LABEL: Record<PdfVersion['status'], string> = {
  gerando: 'Gerando',
  pendente_aprovacao: 'Aguardando aprovação',
  aprovado: 'Aprovado · agendado',
  rejeitado: 'Rejeitado',
  entregue: 'Entregue à cliente',
  falhou: 'Falhou',
};

const STATUS_COLOR: Record<PdfVersion['status'], string> = {
  gerando: 'text-sara-text-muted',
  pendente_aprovacao: 'text-sara-warning',
  aprovado: 'text-sara-accent',
  rejeitado: 'text-sara-text-muted',
  entregue: 'text-sara-success',
  falhou: 'text-sara-danger',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateOnly(iso: string | null): string {
  if (!iso) return '—';
  // Pega só a parte YYYY-MM-DD da string ISO pra evitar fuso virar dia antes
  // (o backend salva como Date com hora 12:00 UTC pra estabilidade).
  const datePart = iso.split('T')[0]!;
  const [y, m, d] = datePart.split('-');
  return `${d}/${m}/${y}`;
}

function dateForInput(iso: string | null): string {
  if (!iso) return '';
  return iso.split('T')[0]!;
}

const ORIGEM_META: Record<Origem, { label: string; cls: string }> = {
  sara: { label: 'pediu à Sara', cls: 'bg-sara-accent/15 text-sara-accent' },
  webapp: { label: 'webapp', cls: 'bg-sara-warning/15 text-sara-warning' },
  avulso: { label: 'você criou', cls: 'bg-sara-surface-hover text-sara-text-muted' },
};

/**
 * Status do Resend que provam que o PDF chegou na caixa da cliente (espelha
 * EMAIL_STATUS_ENTREGUE do backend, que classifica o bucket). Ficam de fora
 * `pending`/`delivery_delayed` (não chegou) e `bounced`/`complained` (voltou).
 */
const EMAIL_STATUS_ENTREGUE = new Set(['sent', 'delivered', 'opened', 'clicked']);

/** Rótulo + cor do status de uma linha, derivado do bucket + PDF + cálculo. */
function rowStatus(m: MapaInbox): { label: string; color: string } {
  const s = m.latestPdf?.status;
  if (m.bucket === 'precisa_de_voce') {
    if (s === 'pendente_aprovacao')
      return { label: 'Aguardando aprovação', color: 'text-sara-warning' };
    if (s === 'falhou') return { label: 'Falhou ao gerar', color: 'text-sara-danger' };
    if (m.statusCalculo === 'erro') return { label: 'Erro no cálculo', color: 'text-sara-danger' };
    return { label: 'Entrega atrasada', color: 'text-sara-danger' };
  }
  if (m.bucket === 'em_andamento') {
    if (s === 'gerando') return { label: 'Gerando PDF…', color: 'text-sara-text-muted' };
    // "Calculando…" era o rótulo de TUDO que estava em andamento, inclusive do
    // mapa parado há dias esperando um dado que a cliente nunca mandou. Foi a
    // primeira coisa que a dona estranhou em 19/08/2026 ("por que tá assim?"):
    // nada estava calculando. Quando o que falta é dado da cliente, a linha
    // diz o que falta, e "Editar dados" resolve na hora se você souber.
    if (!m.latestPdf && m.cidadeNasc === 'pendente') {
      return { label: 'Falta a cidade', color: 'text-sara-warning' };
    }
    if (!m.latestPdf && m.horaConhecida && !m.horaNasc) {
      return { label: 'Falta a hora', color: 'text-sara-warning' };
    }
    return { label: 'Calculando…', color: 'text-sara-text-muted' };
  }
  if (s === 'entregue' || m.statusCalculo === 'entregue')
    return { label: 'Entregue', color: 'text-sara-success' };
  // A Sara não entregou (janela de 24h fechada, número errado…), mas o PDF saiu
  // por email — é entrega concluída, e o rótulo diz por onde foi.
  if (m.latestPdf && EMAIL_STATUS_ENTREGUE.has(m.latestPdf.emailStatus ?? ''))
    return { label: 'Entregue por email', color: 'text-sara-success' };
  if (s === 'aprovado') {
    // Webapp EN: aprovar já libera o PDF na área "My Birth Chart" do app — não há
    // entrega agendada por WhatsApp, então "agendado" confundiria.
    if (m.origem === 'webapp') return { label: 'Liberado no app', color: 'text-sara-success' };
    // Avulso: você manda à mão; aprovado é aprovado, sem agendamento automático.
    if (m.origem === 'avulso') return { label: 'Aprovado', color: 'text-sara-accent' };
    return { label: 'Aprovado · agendado', color: 'text-sara-accent' };
  }
  return { label: m.statusCalculo, color: 'text-sara-text-muted' };
}

const BUCKET_ORDER: Bucket[] = ['precisa_de_voce', 'em_andamento', 'concluido'];
const BUCKET_META: Record<Bucket, { label: string; icon: string; color: string }> = {
  precisa_de_voce: { label: 'Precisa de você', icon: '🔴', color: 'text-sara-danger' },
  em_andamento: { label: 'Em andamento', icon: '⏳', color: 'text-sara-text-muted' },
  concluido: { label: 'Concluídos', icon: '✅', color: 'text-sara-success' },
};

export function MapasPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [origemFilter, setOrigemFilter] = useState<'' | Origem>('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showConcluidos, setShowConcluidos] = useState(false);
  const [showCriar, setShowCriar] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<MapaInbox[]>({
    queryKey: ['mapas-inbox'],
    queryFn: () => request<MapaInbox[]>('/admin/mapas/inbox'),
    refetchInterval: (q) => {
      const items = (q.state.data ?? []) as MapaInbox[];
      const ativo = items.some(
        (m) =>
          m.latestPdf?.status === 'gerando' ||
          m.statusCalculo === 'pendente' ||
          m.statusCalculo === 'calculando',
      );
      return ativo ? 4_000 : false;
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((m) => {
      if (origemFilter && m.origem !== origemFilter) return false;
      if (!q) return true;
      return (
        (m.nome ?? '').toLowerCase().includes(q) ||
        (m.telefone ?? '').toLowerCase().includes(q) ||
        (m.webappEmail ?? '').toLowerCase().includes(q) ||
        (m.contato?.telefone ?? '').toLowerCase().includes(q)
      );
    });
  }, [data, search, origemFilter]);

  const byBucket = useMemo(() => {
    const groups: Record<Bucket, MapaInbox[]> = {
      precisa_de_voce: [],
      em_andamento: [],
      concluido: [],
    };
    for (const m of filtered) groups[m.bucket].push(m);
    return groups;
  }, [filtered]);

  return (
    <section aria-labelledby="mapas-heading" className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 id="mapas-heading" className="text-2xl font-semibold">
          Mapas Astrais
        </h1>
        <button type="button" className="btn-primary" onClick={() => setShowCriar(true)}>
          ➕ Criar novo mapa
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="block flex-1 min-w-[220px]">
          <span className="label">Buscar</span>
          <input
            type="search"
            className="input w-full"
            placeholder="Nome, telefone ou email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Origem</span>
          <select
            className="input"
            value={origemFilter}
            onChange={(e) => setOrigemFilter(e.target.value as '' | Origem)}
          >
            <option value="">Todas</option>
            <option value="sara">Pediu à Sara</option>
            <option value="webapp">Webapp</option>
            <option value="avulso">Você criou</option>
          </select>
        </label>
      </div>

      {isLoading && <p className="text-sm text-sara-text-muted">Carregando mapas…</p>}
      {isError && (
        <p className="text-sm text-sara-danger">
          Erro carregando mapas.{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            Tentar de novo
          </button>
        </p>
      )}

      {!isLoading &&
        !isError &&
        BUCKET_ORDER.map((bucket) => {
          const items = byBucket[bucket];
          const isConcluidos = bucket === 'concluido';
          if (items.length === 0 && bucket !== 'precisa_de_voce') return null;
          const meta = BUCKET_META[bucket];
          return (
            <div key={bucket} className="space-y-2">
              {isConcluidos ? (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 text-left"
                  onClick={() => setShowConcluidos((v) => !v)}
                  aria-expanded={showConcluidos}
                >
                  <span aria-hidden>{meta.icon}</span>
                  <span className={`text-sm font-semibold ${meta.color}`}>{meta.label}</span>
                  <span className="text-xs text-sara-text-muted">{items.length}</span>
                  <span className="text-xs text-sara-text-muted">{showConcluidos ? '▲' : '▼'}</span>
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span aria-hidden>{meta.icon}</span>
                  <span className={`text-sm font-semibold ${meta.color}`}>{meta.label}</span>
                  <span className="text-xs text-sara-text-muted">{items.length}</span>
                </div>
              )}

              {(!isConcluidos || showConcluidos) &&
                (items.length === 0 ? (
                  <p className="text-sm text-sara-text-muted pl-6">Nada aqui — tudo em dia. ✨</p>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-sara-border">
                    {items.map((m, i) => (
                      <MapaRow
                        key={m.id}
                        mapa={m}
                        isLast={i === items.length - 1}
                        expanded={expanded === m.id}
                        onToggle={() => setExpanded(expanded === m.id ? null : m.id)}
                        onChange={() => void refetch()}
                      />
                    ))}
                  </div>
                ))}
            </div>
          );
        })}

      {showCriar && (
        <CriarMapaModal
          onClose={() => setShowCriar(false)}
          onCreated={() => {
            setShowCriar(false);
            void refetch();
          }}
        />
      )}
    </section>
  );
}

function MapaRow({
  mapa,
  isLast,
  expanded,
  onToggle,
  onChange,
}: {
  mapa: MapaInbox;
  isLast: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChange: () => void;
}): JSX.Element {
  const st = rowStatus(mapa);
  const origem = ORIGEM_META[mapa.origem];
  return (
    <div className={isLast ? '' : 'border-b border-sara-border'}>
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-sara-surface-hover/40"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{mapa.nome || 'Sem nome'}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${origem.cls}`}>
              {origem.label}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-sara-text-muted">
            {formatDateOnly(mapa.dataNasc)}
            {mapa.horaConhecida && mapa.horaNasc ? ` ${mapa.horaNasc}` : ' (sem hora)'} ·{' '}
            {mapa.cidadeNasc}
            {mapa.ufNasc ? `/${mapa.ufNasc}` : ''}
            {mapa.telefone ? ` · ${mapa.telefone}` : ''}
          </div>
        </div>
        <span className={`shrink-0 text-xs ${st.color}`}>{st.label}</span>
        <span className="shrink-0 text-xs text-sara-text-muted">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded &&
        (mapa.origem === 'sara' ? (
          <SaraPanel mapa={mapa} onChange={onChange} />
        ) : (
          <AvulsoPanel mapa={mapa} onChange={onChange} />
        ))}
    </div>
  );
}

// ============== Painel do mapa da SARA (cliente WhatsApp, com versões) ==============

interface LugarCalculado {
  cidade: string | null;
  timezone: string | null;
}

function descreveLugar(l: LugarCalculado): string {
  return `${l.cidade ?? 'lugar desconhecido'} (${l.timezone ?? 'fuso desconhecido'})`;
}

/**
 * Conserta o mapa que nasceu no lugar errado, para os DOIS painéis (cliente da
 * Sara e mapa do app/avulso).
 *
 * O geocoding já confundiu cidades de mesmo nome ("DuBois/PA" calculado em
 * Dubois do Idaho, "louisville/CO" em Louisville do Kentucky), e as coordenadas
 * erradas ficam GRAVADAS: só "Gerar nova versão" devolve texto novo com
 * planetas velhos. Aqui o backend refaz o cálculo e, quando o lugar muda, já
 * enfileira a versão nova sozinho.
 *
 * Nasceu compartilhado porque o botão antigo existia só no painel do cliente de
 * WhatsApp, e quem mais sofre com isso é a cliente americana, que cai no painel
 * do app. Caso Tammy (07/08/2026): escreveu duas vezes e o suporte não tinha
 * botão nenhum na tela dela.
 */
function useRecalcularLugar(mapaId: string, onDone: () => void) {
  return useMutation({
    mutationFn: () =>
      request<{
        antes: LugarCalculado;
        depois: LugarCalculado;
        mudou: boolean;
        regerando: boolean;
      }>(`/admin/mapas/${mapaId}/recalcular`, { method: 'POST' }),
    onSuccess: (r) => {
      alert(
        r.mudou
          ? `O lugar do cálculo mudou. ✅\n\nAntes: ${descreveLugar(r.antes)}\nAgora: ${descreveLugar(r.depois)}\n\nO mapa anterior estava errado. Uma versão nova já está sendo gerada com as posições certas, é só conferir e aprovar quando ficar pronta.`
          : `Nada mudou: o mapa já estava calculado no lugar certo, ${descreveLugar(r.depois)}.\n\nNenhuma versão nova foi gerada.`,
      );
      onDone();
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao recalcular'),
  });
}

function SaraPanel({ mapa, onChange }: { mapa: MapaInbox; onChange: () => void }): JSX.Element {
  const mapaId = mapa.id;
  const queryClient = useQueryClient();
  const [showRejectFor, setShowRejectFor] = useState<string | null>(null);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [editing, setEditing] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const initialForm = () => ({
    primeiroNome: mapa.cliente?.primeiroNome ?? '',
    nomeCompleto: mapa.cliente?.nomeCompleto ?? '',
    dataNasc: dateForInput(mapa.dataNasc),
    horaNasc: mapa.horaConhecida && mapa.horaNasc ? mapa.horaNasc : '',
    horaConhecida: mapa.horaConhecida,
    cidadeNasc: mapa.cidadeNasc,
    ufNasc: mapa.ufNasc ?? '',
    paisNasc: (mapa.paisNasc || 'BR').toUpperCase(),
  });
  const [editForm, setEditForm] = useState(initialForm);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mapas-inbox'] });
    queryClient.invalidateQueries({ queryKey: ['mapa-pdfs', mapaId] });
    onChange();
  };

  const { data, isLoading, isError, refetch } = useQuery<PdfVersion[]>({
    queryKey: ['mapa-pdfs', mapaId],
    queryFn: () => request<PdfVersion[]>(`/admin/mapas/${mapaId}/pdfs`),
    refetchInterval: (q) => {
      const items = (q.state.data ?? []) as PdfVersion[];
      return items.some((p) => p.status === 'gerando') ? 4_000 : false;
    },
  });

  const editarDados = useMutation({
    mutationFn: (regerar: boolean) =>
      request<{ ok: true; regenerated?: { pdfId: string; versao: number } }>(
        `/admin/mapas/${mapaId}/dados`,
        {
          method: 'POST',
          body: {
            primeiroNome: editForm.primeiroNome.trim() || undefined,
            nomeCompleto: editForm.nomeCompleto.trim() || undefined,
            dataNasc: editForm.dataNasc || undefined,
            horaNasc: editForm.horaConhecida ? editForm.horaNasc : null,
            horaConhecida: editForm.horaConhecida,
            cidadeNasc: editForm.cidadeNasc.trim() || undefined,
            ufNasc: editForm.ufNasc.trim().toUpperCase() || null,
            paisNasc: editForm.paisNasc || undefined,
            regerar,
          },
        },
      ),
    onSuccess: (res) => {
      invalidate();
      setEditing(false);
      if (res.regenerated) {
        alert(
          `Dados salvos! Nova versão (v${res.regenerated.versao}) sendo gerada com os dados corrigidos.`,
        );
      } else {
        alert(
          'Dados salvos. Pra gerar nova versão com esses dados, clica em "Gerar nova versão" abaixo.',
        );
      }
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Falha salvando: ${msg}`);
    },
  });

  const gerarMutation = useMutation({
    mutationFn: () =>
      request<{ pdfId: string; versao: number }>(`/admin/mapas/${mapaId}/pdfs/gerar`, {
        method: 'POST',
      }),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Falha gerando: ${msg}`);
    },
  });

  const aprovarMutation = useMutation({
    mutationFn: (pdfId: string) =>
      request<{ ok: true; deliverAt: string }>(`/admin/mapas/pdfs/${pdfId}/aprovar`, {
        method: 'POST',
      }),
    onSuccess: (res) => {
      invalidate();
      const dt = new Date(res.deliverAt).toLocaleString('pt-BR');
      alert(`Aprovado! Sara entrega automaticamente em ${dt}.`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Falha aprovando: ${msg}`);
    },
  });

  const rejeitarMutation = useMutation({
    mutationFn: ({ pdfId, feedback }: { pdfId: string; feedback: string }) =>
      request<{ ok: true; novaVersao: number | null }>(`/admin/mapas/pdfs/${pdfId}/rejeitar`, {
        method: 'POST',
        body: { feedback },
      }),
    onSuccess: (res) => {
      invalidate();
      setShowRejectFor(null);
      setRejectFeedback('');
      if (res.novaVersao) {
        alert(`Versão rejeitada. Gerando v${res.novaVersao} com seu feedback…`);
      }
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Falha rejeitando: ${msg}`);
    },
  });

  // Envio por EMAIL do PDF (versão mais recente) — canal independente do
  // WhatsApp, sem janela de 24h. email vazio = usa o email do cadastro; ou
  // digita um na hora.
  const enviarEmail = useMutation({
    mutationFn: (email: string) =>
      request<{ enviado: true; email: string }>(`/admin/mapas/${mapaId}/enviar-email`, {
        method: 'POST',
        body: email ? { email } : {},
      }),
    onSuccess: (r) => {
      alert(`Mapa enviado por email pra ${r.email}. ✅`);
      setEmailing(false);
      setEmailValue('');
      invalidate();
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao enviar email'),
  });

  const recalc = useRecalcularLugar(mapaId, invalidate);

  // Reenvio manual no WhatsApp — mesmo endpoint dos avulsos; no mapa da Sara ele
  // cai no cliente (contatoEnvio ?? cliente no backend). Respeita janela de 24h
  // (Cloud) e já manda por email como fallback quando a janela está fechada.
  const enviarWhatsapp = useMutation({
    mutationFn: () =>
      request<{ enviado: true; canal: string }>(`/admin/mapas/${mapaId}/enviar-agora`, {
        method: 'POST',
      }),
    onSuccess: (r) => {
      alert(`Mapa reenviado por WhatsApp (${r.canal}). ✅`);
      invalidate();
    },
    onError: (e: unknown) =>
      alert(e instanceof Error ? e.message : 'Falha ao reenviar no WhatsApp'),
  });

  const versoes = data ?? [];

  return (
    <div className="space-y-4 border-t border-sara-divider bg-sara-surface-hover/30 p-4">
      {/* Bloco de dados da cliente / mapa — editável */}
      <div className="space-y-2 rounded border border-sara-divider/50 bg-sara-bg p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Dados do mapa</h3>
          {!editing && (
            <button type="button" className="btn-ghost text-xs" onClick={() => setEditing(true)}>
              ✏ Editar
            </button>
          )}
        </div>

        {!editing ? (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs text-sara-text-muted">Nome completo</dt>
              <dd>{mapa.cliente?.nomeCompleto || mapa.cliente?.primeiroNome || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-sara-text-muted">Telefone</dt>
              <dd>{mapa.telefone ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-sara-text-muted">Data de nascimento</dt>
              <dd>{formatDateOnly(mapa.dataNasc)}</dd>
            </div>
            <div>
              <dt className="text-xs text-sara-text-muted">Hora de nascimento</dt>
              <dd>
                {mapa.horaConhecida && mapa.horaNasc ? mapa.horaNasc : 'desconhecida (mapa solar)'}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-sara-text-muted">Cidade de nascimento</dt>
              <dd>
                {mapa.cidadeNasc}
                {mapa.ufNasc ? `/${mapa.ufNasc}` : ''}
              </dd>
            </div>
          </dl>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Primeiro nome</span>
              <input
                type="text"
                className="input w-full"
                value={editForm.primeiroNome}
                onChange={(e) => setEditForm({ ...editForm, primeiroNome: e.target.value })}
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Nome completo</span>
              <input
                type="text"
                className="input w-full"
                value={editForm.nomeCompleto}
                onChange={(e) => setEditForm({ ...editForm, nomeCompleto: e.target.value })}
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Data de nascimento</span>
              <input
                type="date"
                className="input w-full"
                value={editForm.dataNasc}
                onChange={(e) => setEditForm({ ...editForm, dataNasc: e.target.value })}
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Hora de nascimento</span>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  className="input flex-1"
                  value={editForm.horaNasc}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      horaNasc: e.target.value,
                      horaConhecida: e.target.value !== '',
                    })
                  }
                  disabled={!editForm.horaConhecida}
                />
                <label className="flex items-center gap-1 whitespace-nowrap text-xs">
                  <input
                    type="checkbox"
                    checked={!editForm.horaConhecida}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        horaConhecida: !e.target.checked,
                        horaNasc: e.target.checked ? '' : editForm.horaNasc,
                      })
                    }
                  />
                  sem hora
                </label>
              </div>
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Cidade</span>
              <input
                type="text"
                className="input w-full"
                value={editForm.cidadeNasc}
                onChange={(e) => setEditForm({ ...editForm, cidadeNasc: e.target.value })}
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">UF (sigla)</span>
              <input
                type="text"
                className="input w-full"
                maxLength={2}
                value={editForm.ufNasc}
                onChange={(e) => setEditForm({ ...editForm, ufNasc: e.target.value.toUpperCase() })}
                placeholder="SC"
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">País</span>
              <select
                className="input w-full"
                value={editForm.paisNasc}
                onChange={(e) => setEditForm({ ...editForm, paisNasc: e.target.value })}
              >
                {PAISES.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex flex-wrap gap-2 pt-2 sm:col-span-2">
              <button
                type="button"
                className="btn-primary text-xs"
                disabled={editarDados.isPending}
                onClick={() => editarDados.mutate(true)}
              >
                {editarDados.isPending ? 'Salvando…' : '💾 Salvar e regerar PDF'}
              </button>
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={editarDados.isPending}
                onClick={() => editarDados.mutate(false)}
              >
                Salvar sem regerar
              </button>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => {
                  setEditing(false);
                  setEditForm(initialForm());
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">PDFs do Mapa</h3>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => recalc.mutate()}
            disabled={recalc.isPending}
            title="Use quando a cliente disser que o mapa saiu com a cidade ou o estado errado. Refaz o cálculo e, se o lugar mudar, já gera a versão nova."
          >
            {recalc.isPending ? 'Conferindo o lugar…' : '📍 Cidade saiu errada?'}
          </button>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => gerarMutation.mutate()}
            disabled={gerarMutation.isPending}
          >
            {gerarMutation.isPending ? 'Gerando…' : '+ Gerar nova versão'}
          </button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-sara-text-muted">Carregando versões…</p>}
      {isError && (
        <p className="text-sm text-sara-danger">
          Erro carregando PDFs.{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            Tentar de novo
          </button>
        </p>
      )}

      {!isLoading && !isError && versoes.length === 0 && (
        <p className="text-sm text-sara-text-muted">
          Nenhuma versão gerada ainda. Clica &quot;Gerar nova versão&quot; pra começar.
        </p>
      )}

      <div className="space-y-2">
        {versoes.map((v) => (
          <div
            key={v.id}
            className="space-y-2 rounded border border-sara-divider/50 bg-sara-bg p-3"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="flex items-baseline gap-3">
                <span className="text-sm font-semibold">Versão {v.versao}</span>
                <span className={`text-xs ${STATUS_COLOR[v.status]}`}>
                  {STATUS_LABEL[v.status]}
                </span>
                {v.approvedBy === 'auto-aprovacao' && (
                  <span
                    className="text-xs rounded px-1.5 py-0.5 bg-sara-surface text-sara-text-muted"
                    title="Aprovado pelo robô, dentro da janela de horário — ninguém leu antes de ir pra cliente"
                  >
                    🤖 robô
                  </span>
                )}
                {v.sizeKb && <span className="text-xs text-sara-text-muted">{v.sizeKb} KB</span>}
                {v.llmCusto !== null && (
                  <span className="text-xs text-sara-text/50">US$ {v.llmCusto.toFixed(3)}</span>
                )}
              </div>
              <div className="text-xs text-sara-text-muted">
                Gerado: {formatDate(v.generatedAt)}
              </div>
            </div>

            {v.feedback && (
              <div className="border-l-2 border-sara-warning bg-sara-warning/5 px-3 py-2 text-xs">
                <span className="font-medium">Feedback que originou esta versão:</span> {v.feedback}
              </div>
            )}

            {v.errorMessage && (
              <div className="space-y-2 border-l-2 border-sara-danger bg-sara-danger/5 px-3 py-2 text-xs text-sara-danger">
                <div>
                  <span className="font-medium">Não foi possível gerar esta versão.</span>{' '}
                  {v.errorMessage}
                </div>
                {v.status === 'falhou' && (
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    onClick={() => gerarMutation.mutate()}
                    disabled={gerarMutation.isPending}
                  >
                    {gerarMutation.isPending ? 'Tentando…' : '🔄 Tentar novamente'}
                  </button>
                )}
              </div>
            )}

            {(v.status === 'aprovado' ||
              v.status === 'entregue' ||
              v.status === 'pendente_aprovacao' ||
              !!v.emailEnviadoEm) && (
              <div className="space-y-0.5 rounded bg-sara-surface-hover/40 px-2 py-1.5 text-xs">
                {v.status === 'entregue' && v.deliveredAt ? (
                  <p className="text-sara-success">
                    ✅ WhatsApp: entregue em {formatDate(v.deliveredAt)} pela Sara.
                  </p>
                ) : v.status === 'aprovado' && v.deliverAt ? (
                  new Date(v.deliverAt).getTime() < Date.now() ? (
                    // Se o email já saiu, a entrega está feita: o WhatsApp não sai
                    // mais (janela de 24h fechada) e cobrar reenvio seria ruído.
                    EMAIL_STATUS_ENTREGUE.has(v.emailStatus ?? '') ? (
                      <p className="text-sara-text-muted">
                        ⚪ WhatsApp: não saiu (entrega agendada pra {formatDate(v.deliverAt)}
                        ). Foi entregue por email.
                      </p>
                    ) : (
                      <p className="text-sara-danger">
                        ⚠️ WhatsApp: era pra entregar em {formatDate(v.deliverAt)} (já passou) e
                        ainda NÃO foi entregue. Reenvie por email abaixo ou pelo WhatsApp
                        manualmente.
                      </p>
                    )
                  ) : (
                    <p className="text-sara-accent">
                      ⏳ WhatsApp: entrega agendada pra {formatDate(v.deliverAt)}.
                    </p>
                  )
                ) : (
                  <p className="text-sara-text-muted">⚪ WhatsApp: não entregue ainda.</p>
                )}
                {v.emailEnviadoEm ? (
                  <p className="text-sara-success">
                    ✅ Email: enviado em {formatDate(v.emailEnviadoEm)}
                    {v.emailStatus && v.emailStatus !== 'sent' && v.emailStatus !== 'delivered'
                      ? ` (${v.emailStatus})`
                      : ''}
                    .
                  </p>
                ) : (
                  <p className="text-sara-text-muted">⚪ Email: não enviado.</p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {v.sizeKb && (
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => downloadPdf(v.id)}
                >
                  📄 Ver / Baixar PDF
                </button>
              )}
              {v.status === 'pendente_aprovacao' && (
                <>
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    onClick={() => {
                      if (
                        confirm(
                          `Aprovar v${v.versao}? A Sara vai entregar automaticamente o PDF pela cliente em até 48h úteis após a solicitação dela.`,
                        )
                      ) {
                        aprovarMutation.mutate(v.id);
                      }
                    }}
                    disabled={aprovarMutation.isPending}
                  >
                    ✓ Aprovar
                  </button>
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => setShowRejectFor(v.id)}
                    disabled={rejeitarMutation.isPending}
                  >
                    ✗ Rejeitar
                  </button>
                </>
              )}
              {data?.[0]?.id === v.id && !!v.sizeKb && (
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={enviarWhatsapp.isPending}
                  onClick={() => {
                    if (confirm('Reenviar este mapa no WhatsApp da cliente agora?')) {
                      enviarWhatsapp.mutate();
                    }
                  }}
                  title="Reenviar o PDF no WhatsApp da cliente (respeita janela de 24h; manda email se fechada)"
                >
                  {enviarWhatsapp.isPending ? 'Reenviando…' : '📲 Reenviar no WhatsApp'}
                </button>
              )}
              {data?.[0]?.id === v.id && !!v.sizeKb && (
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={enviarEmail.isPending}
                  onClick={() => {
                    setEmailValue('');
                    setEmailing((x) => !x);
                  }}
                  title="Enviar o PDF por email (sem janela de 24h)"
                >
                  📧 Enviar por email
                </button>
              )}
            </div>

            {emailing && data?.[0]?.id === v.id && (
              <div className="flex flex-wrap items-center gap-2 rounded border border-sara-border bg-sara-surface-hover/40 p-2">
                <input
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder="deixe vazio pra usar o email do cadastro, ou digite um"
                  aria-label="Email pra enviar o mapa"
                  className="min-w-[200px] flex-1 rounded border border-sara-border bg-sara-surface px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={enviarEmail.isPending}
                  onClick={() => enviarEmail.mutate(emailValue.trim())}
                >
                  {enviarEmail.isPending ? 'Enviando…' : 'Enviar email'}
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => setEmailing(false)}
                >
                  Cancelar
                </button>
              </div>
            )}

            {showRejectFor === v.id && (
              <div className="space-y-2 rounded bg-sara-surface-hover/60 p-3">
                <label className="block text-xs">
                  <span className="mb-1 block font-medium">O que tá errado nesta versão?</span>
                  <textarea
                    className="input w-full text-sm"
                    rows={3}
                    value={rejectFeedback}
                    onChange={(e) => setRejectFeedback(e.target.value)}
                    placeholder='Ex: "A parte de Júpiter ficou superficial, refaz com mais profundidade. Verifica também a posição da Lua que parece errada."'
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    onClick={() => {
                      if (rejectFeedback.trim().length < 5) {
                        alert('Feedback precisa ter pelo menos 5 caracteres.');
                        return;
                      }
                      rejeitarMutation.mutate({ pdfId: v.id, feedback: rejectFeedback.trim() });
                    }}
                    disabled={rejeitarMutation.isPending}
                  >
                    {rejeitarMutation.isPending
                      ? 'Rejeitando + regerando…'
                      : 'Rejeitar e regerar com esse feedback'}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => {
                      setShowRejectFor(null);
                      setRejectFeedback('');
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== Painel do mapa AVULSO / WEBAPP (uma versão de PDF) ==============

function downloadPdf(pdfId: string): void {
  void (async () => {
    try {
      // Fetch autenticado (Authorization Bearer JWT) e abre como blob URL — link
      // <a href> não enviaria o token.
      const blob = await requestBlob(`/admin/mapas/pdfs/${pdfId}/download`);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoga depois de uns segundos pra liberar memória.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Falha ao baixar PDF');
    }
  })();
}

function AvulsoPanel({ mapa, onChange }: { mapa: MapaInbox; onChange: () => void }): JSX.Element {
  const [linking, setLinking] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    dataNasc: dateForInput(mapa.dataNasc),
    horaNasc: mapa.horaConhecida && mapa.horaNasc ? mapa.horaNasc : '',
    horaConhecida: mapa.horaConhecida,
    cidadeNasc: mapa.cidadeNasc,
    ufNasc: mapa.ufNasc ?? '',
    paisNasc: (mapa.paisNasc || 'US').toUpperCase(),
  });
  const pdf = mapa.latestPdf;
  const status = pdf?.status;
  const pronto = status === 'pendente_aprovacao' || status === 'aprovado' || status === 'entregue';
  const contato = mapa.contato;
  const podeEnviar =
    pronto && !!contato && (contato.canal !== 'whatsapp_cloud' || contato.janela24hAberta);

  // Aprovar o mapa avulso: LIBERA o PDF no app da pessoa (área "My Birth Chart")
  // e faz a Sara avisar no chat (gancho notifyWebappBirthChartReady no approve).
  const aprovar = useMutation({
    mutationFn: () =>
      request<{ ok: true }>(`/admin/mapas/pdfs/${pdf!.id}/aprovar`, { method: 'POST' }),
    onSuccess: () => {
      alert('Mapa aprovado! ✅ O PDF foi liberado no app da pessoa e a Sara avisa no chat.');
      onChange();
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao aprovar'),
  });

  const enviar = useMutation({
    mutationFn: () =>
      request<{ enviado: true; canal: string }>(`/admin/mapas/${mapa.id}/enviar-agora`, {
        method: 'POST',
      }),
    onSuccess: (r) => {
      alert(`Mapa enviado por WhatsApp (${r.canal}). ✅`);
      onChange();
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao enviar'),
  });

  // Envio por EMAIL — canal independente do WhatsApp, SEM janela de 24h.
  const enviarEmail = useMutation({
    mutationFn: (email: string) =>
      request<{ enviado: true; email: string }>(`/admin/mapas/${mapa.id}/enviar-email`, {
        method: 'POST',
        body: { email },
      }),
    onSuccess: (r) => {
      alert(`Mapa enviado por email pra ${r.email}. ✅`);
      setEmailing(false);
      onChange();
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao enviar email'),
  });
  const desvincular = useMutation({
    mutationFn: () => request(`/admin/mapas/${mapa.id}/contato-envio`, { method: 'DELETE' }),
    onSuccess: () => onChange(),
  });
  const vincular = useMutation({
    mutationFn: (clienteId: string) =>
      request(`/admin/mapas/${mapa.id}/contato-envio`, { method: 'POST', body: { clienteId } }),
    onSuccess: () => {
      setLinking(false);
      onChange();
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao vincular'),
  });

  // Editar data/hora/local e regerar o PDF com os dados corrigidos. Pra mapa
  // avulso/webapp o nome vive em nomeAvulso e não é editado aqui.
  const editarDados = useMutation({
    mutationFn: () =>
      request<{ ok: true; regenerated?: { versao: number } }>(`/admin/mapas/${mapa.id}/dados`, {
        method: 'POST',
        body: {
          dataNasc: editForm.dataNasc,
          horaNasc: editForm.horaConhecida ? editForm.horaNasc || null : null,
          horaConhecida: editForm.horaConhecida,
          cidadeNasc: editForm.cidadeNasc.trim(),
          ufNasc: editForm.ufNasc.trim() || null,
          paisNasc: editForm.paisNasc,
          regerar: true,
        },
      }),
    onSuccess: (res) => {
      alert(
        res.regenerated
          ? `Dados salvos! Uma nova versão (v${res.regenerated.versao}) está sendo gerada com os dados corrigidos. Confira e aprove quando ficar pronta.`
          : 'Dados salvos.',
      );
      setEditing(false);
      onChange();
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao salvar os dados'),
  });

  const recalcLugar = useRecalcularLugar(mapa.id, onChange);

  // "Rejeitar e devolver pro cliente" — só pra mapa do webapp. Apaga o mapa: o
  // app volta a mostrar o formulário e a pessoa reenvia os dados.
  const devolver = useMutation({
    mutationFn: () =>
      request<{ ok: true; webappEmail: string }>(`/admin/mapas/${mapa.id}/devolver-webapp`, {
        method: 'POST',
      }),
    onSuccess: (r) => {
      alert(
        `Devolvido. ${r.webappEmail} vai ver o formulário do Mapa de novo no app e poderá preencher os dados corretos.`,
      );
      onChange();
    },
    onError: (e: unknown) =>
      alert(e instanceof Error ? e.message : 'Falha ao devolver pro cliente'),
  });

  // "Excluir mapa" — só pra avulso (criado à mão no painel). É a única saída da
  // inbox pra um avulso cuja geração falhou ou que virou duplicata: sem PDF em
  // 'pendente_aprovacao' não há aprovar nem rejeitar, e a linha ficaria em
  // "Precisa de você" pra sempre.
  const excluir = useMutation({
    mutationFn: () => request<{ ok: true }>(`/admin/mapas/${mapa.id}`, { method: 'DELETE' }),
    onSuccess: () => onChange(),
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao excluir o mapa'),
  });

  // Opções de país: a lista curta + o país atual se não estiver nela (ex.:
  // Myanmar/MM veio do app mas não está em PAISES) — pra não perder o dado.
  const paisOptions =
    editForm.paisNasc && !PAISES.some((p) => p.code === editForm.paisNasc)
      ? [{ code: editForm.paisNasc, nome: editForm.paisNasc }, ...PAISES]
      : PAISES;

  return (
    <div className="space-y-3 border-t border-sara-divider bg-sara-surface-hover/30 p-4">
      {mapa.preenchidoEm && (
        <div className="text-xs text-sara-text-muted">
          📝 Preenchido em {formatDate(mapa.preenchidoEm)}
          {mapa.webappEmail ? ` · ${mapa.webappEmail}` : ''}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {status === 'pendente_aprovacao' && pdf && (
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={aprovar.isPending}
            onClick={() => {
              if (
                confirm(
                  'Aprovar este mapa?\n\nIsso LIBERA o PDF no app da pessoa (área "My Birth Chart") e a Sara avisa no chat que ficou pronto. Confira o PDF antes (botão "Baixar PDF").',
                )
              ) {
                aprovar.mutate();
              }
            }}
            title="Aprovar → libera no app da pessoa + Sara avisa no chat"
          >
            {aprovar.isPending ? 'Aprovando…' : '✓ Aprovar (libera no app)'}
          </button>
        )}
        {pronto && pdf?.temArquivo && (
          <button type="button" className="btn-ghost text-xs" onClick={() => downloadPdf(pdf.id)}>
            ⬇ Baixar PDF
          </button>
        )}
        {pronto && (
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={!podeEnviar || enviar.isPending}
            onClick={() => enviar.mutate()}
            title={
              !contato
                ? 'Vincule um contato primeiro'
                : !podeEnviar
                  ? 'Janela de 24h fechada — peça pra pessoa mandar um oi'
                  : 'Enviar no WhatsApp agora'
            }
          >
            {enviar.isPending ? 'Enviando…' : '📲 Enviar agora'}
          </button>
        )}
        {pronto && (
          <button
            type="button"
            className="btn-ghost text-xs"
            disabled={enviarEmail.isPending}
            onClick={() => {
              setEmailValue(contato?.email ?? '');
              setEmailing((v) => !v);
            }}
            title="Enviar o PDF por email (sem janela de 24h)"
          >
            📧 Enviar por email
          </button>
        )}
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => setEditing((v) => !v)}
          title="Corrigir data, hora ou local e gerar o mapa de novo"
        >
          {editing ? 'Fechar edição' : '✏ Editar dados'}
        </button>
        {/* Serve pro caso em que os dados digitados JÁ ESTÃO certos e mesmo
            assim o mapa saiu na cidade errada. Aí "Editar dados" não resolve:
            não há o que corrigir, então nada recalcula. */}
        <button
          type="button"
          className="btn-ghost text-xs"
          disabled={recalcLugar.isPending}
          onClick={() => recalcLugar.mutate()}
          title="Use quando a cliente disser que o mapa saiu com a cidade ou o estado errado, mesmo tendo digitado certo. Refaz o cálculo e, se o lugar mudar, já gera a versão nova."
        >
          {recalcLugar.isPending ? 'Conferindo o lugar…' : '📍 Cidade saiu errada?'}
        </button>
        {mapa.webappEmail && (
          <button
            type="button"
            className="btn-ghost text-xs text-sara-danger"
            disabled={devolver.isPending}
            onClick={() => {
              if (
                confirm(
                  `Devolver este mapa pro cliente preencher de novo?\n\nO mapa atual será APAGADO e ${mapa.webappEmail} verá o formulário do Mapa de novo no app, pra digitar os dados corretos. Use quando os dados vieram errados.`,
                )
              ) {
                devolver.mutate();
              }
            }}
            title="Apaga o mapa e faz o app mostrar o formulário de novo pro cliente"
          >
            {devolver.isPending ? 'Devolvendo…' : '↩ Rejeitar (devolver pro cliente)'}
          </button>
        )}
        {!mapa.webappEmail && (
          <button
            type="button"
            className="btn-ghost text-xs text-sara-danger"
            disabled={excluir.isPending}
            onClick={() => {
              if (
                confirm(
                  `Excluir este mapa de ${mapa.nome ?? 'sem nome'}?\n\nO registro e todos os PDFs gerados são APAGADOS do painel — não dá pra desfazer. Use quando a geração falhou ou o mapa foi criado por engano (duplicata).`,
                )
              ) {
                excluir.mutate();
              }
            }}
            title="Apaga o mapa e os PDFs — tira a linha da lista de vez"
          >
            {excluir.isPending ? 'Excluindo…' : '🗑 Excluir mapa'}
          </button>
        )}
      </div>

      {pdf?.errorMessage && (
        <div className="border-l-2 border-sara-danger bg-sara-danger/5 px-3 py-2 text-xs text-sara-danger">
          {pdf.errorMessage}
        </div>
      )}

      {editing && (
        <div className="space-y-3 rounded border border-sara-border bg-sara-surface-hover/40 p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Data de nascimento</span>
              <input
                type="date"
                className="input w-full"
                value={editForm.dataNasc}
                onChange={(e) => setEditForm({ ...editForm, dataNasc: e.target.value })}
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Hora de nascimento</span>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  className="input flex-1"
                  value={editForm.horaNasc}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      horaNasc: e.target.value,
                      horaConhecida: e.target.value !== '',
                    })
                  }
                  disabled={!editForm.horaConhecida}
                />
                <label className="flex items-center gap-1 whitespace-nowrap text-xs">
                  <input
                    type="checkbox"
                    checked={!editForm.horaConhecida}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        horaConhecida: !e.target.checked,
                        horaNasc: e.target.checked ? '' : editForm.horaNasc,
                      })
                    }
                  />
                  sem hora
                </label>
              </div>
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Cidade</span>
              <input
                type="text"
                className="input w-full"
                value={editForm.cidadeNasc}
                onChange={(e) => setEditForm({ ...editForm, cidadeNasc: e.target.value })}
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-sara-text-muted">Estado/UF (sigla)</span>
              <input
                type="text"
                className="input w-full"
                maxLength={4}
                value={editForm.ufNasc}
                onChange={(e) => setEditForm({ ...editForm, ufNasc: e.target.value.toUpperCase() })}
                placeholder="LA"
              />
            </label>
            <label className="block text-xs sm:col-span-2">
              <span className="mb-1 block text-sara-text-muted">País</span>
              <select
                className="input w-full"
                value={editForm.paisNasc}
                onChange={(e) => setEditForm({ ...editForm, paisNasc: e.target.value })}
              >
                {paisOptions.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={
                editarDados.isPending || !editForm.dataNasc || editForm.cidadeNasc.trim().length < 2
              }
              onClick={() => editarDados.mutate()}
            >
              {editarDados.isPending ? 'Salvando…' : '💾 Salvar e gerar de novo'}
            </button>
            <button type="button" className="btn-ghost text-xs" onClick={() => setEditing(false)}>
              Cancelar
            </button>
          </div>
          <p className="text-xs text-sara-text-muted">
            Ao salvar, o mapa é refeito com os dados corrigidos e volta pra aprovação. Confira e
            aprove a nova versão.
          </p>
        </div>
      )}

      {emailing && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-sara-border bg-sara-surface-hover/40 p-2">
          <input
            type="email"
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            placeholder="email@dapessoa.com"
            aria-label="Email pra enviar o mapa"
            className="min-w-[180px] flex-1 rounded border border-sara-border bg-sara-surface px-2 py-1 text-sm"
          />
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={enviarEmail.isPending || !emailValue.trim()}
            onClick={() => enviarEmail.mutate(emailValue.trim())}
          >
            {enviarEmail.isPending ? 'Enviando…' : 'Enviar email'}
          </button>
          <button type="button" className="btn-ghost text-xs" onClick={() => setEmailing(false)}>
            Cancelar
          </button>
        </div>
      )}

      <div className="border-t border-sara-border pt-2 text-xs">
        {contato ? (
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className="text-sara-text-muted">Contato: </span>
              <span className="font-medium">{contato.nome}</span> · {contato.telefone}
              {contato.canal === 'whatsapp_cloud' && (
                <span
                  className={contato.janela24hAberta ? 'text-sara-success' : 'text-sara-danger'}
                >
                  {' · '}
                  {contato.janela24hAberta ? 'janela 24h aberta' : 'janela 24h fechada'}
                </span>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              <button type="button" className="btn-ghost" onClick={() => setLinking(true)}>
                Trocar
              </button>
              <button
                type="button"
                className="btn-ghost text-sara-danger"
                onClick={() => desvincular.mutate()}
              >
                Remover
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="btn-ghost" onClick={() => setLinking(true)}>
            🔗 Vincular contato pra envio
          </button>
        )}
        {linking && (
          <div className="mt-2">
            <ContatoPicker
              onPick={(c) => vincular.mutate(c.id)}
              onCancel={() => setLinking(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ContatoPicker({
  onPick,
  onCancel,
}: {
  onPick: (c: ContatoBusca) => void;
  onCancel: () => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  const { data } = useQuery<{ items: ContatoBusca[] }>({
    queryKey: ['contato-busca', q],
    queryFn: () =>
      request<{ items: ContatoBusca[] }>(
        `/admin/clientes?search=${encodeURIComponent(q.trim())}&pageSize=8`,
      ),
    enabled: q.trim().length >= 3,
  });
  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <input
          className="input flex-1 text-xs"
          placeholder="Buscar por número ou nome…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <button type="button" className="btn-ghost text-xs" onClick={onCancel}>
          ✕
        </button>
      </div>
      {q.trim().length >= 3 && (
        <div className="max-h-44 overflow-y-auto rounded border border-sara-border">
          {(data?.items ?? []).length === 0 ? (
            <div className="px-2 py-1 text-xs text-sara-text-muted">Nenhum contato encontrado.</div>
          ) : (
            (data?.items ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                className="block w-full px-2 py-1 text-left text-xs hover:bg-sara-bg"
                onClick={() => onPick(c)}
              >
                <span className="font-medium">{c.primeiroNome || 'sem nome'}</span> · {c.telefone}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CriarMapaModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [form, setForm] = useState({
    nomeCompleto: '',
    dataNasc: '',
    horaNasc: '',
    semHora: false,
    cidadeNasc: '',
    ufNasc: '',
    paisNasc: 'BR',
  });
  const [contato, setContato] = useState<ContatoBusca | null>(null);
  const [picking, setPicking] = useState(false);

  const criar = useMutation({
    mutationFn: () =>
      request<{ mapaId: string }>(`/admin/mapas/avulso`, {
        method: 'POST',
        body: {
          nomeCompleto: form.nomeCompleto.trim(),
          dataNasc: form.dataNasc,
          horaNasc: form.semHora ? null : form.horaNasc || null,
          horaConhecida: !form.semHora && !!form.horaNasc,
          cidadeNasc: form.cidadeNasc.trim(),
          ufNasc: form.ufNasc.trim().toUpperCase() || null,
          paisNasc: form.paisNasc || null,
          contatoEnvioId: contato?.id ?? null,
        },
      }),
    onSuccess: () => onCreated(),
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Falha ao criar mapa'),
  });

  const valido =
    form.nomeCompleto.trim().length >= 2 &&
    !!form.dataNasc &&
    form.cidadeNasc.trim().length >= 2 &&
    (form.semHora || !!form.horaNasc);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-xl bg-sara-surface p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Criar novo mapa astral</h3>
          <button type="button" className="btn-ghost" onClick={onClose}>
            ✕
          </button>
        </div>
        <label className="block">
          <span className="label">Nome completo</span>
          <input
            className="input w-full"
            value={form.nomeCompleto}
            onChange={(e) => setForm({ ...form, nomeCompleto: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="label">Data de nascimento</span>
          <input
            type="date"
            className="input w-full"
            value={form.dataNasc}
            onChange={(e) => setForm({ ...form, dataNasc: e.target.value })}
          />
        </label>
        <div className="flex items-end gap-3">
          <label className="block flex-1">
            <span className="label">Hora</span>
            <input
              type="time"
              className="input w-full"
              value={form.horaNasc}
              disabled={form.semHora}
              onChange={(e) => setForm({ ...form, horaNasc: e.target.value })}
            />
          </label>
          <label className="flex shrink-0 items-center gap-1 pb-2 text-xs">
            <input
              type="checkbox"
              checked={form.semHora}
              onChange={(e) => setForm({ ...form, semHora: e.target.checked })}
            />{' '}
            sem hora (solar)
          </label>
        </div>
        <div className="flex gap-2">
          <label className="block flex-1">
            <span className="label">Cidade de nascimento</span>
            <input
              className="input w-full"
              value={form.cidadeNasc}
              onChange={(e) => setForm({ ...form, cidadeNasc: e.target.value })}
            />
          </label>
          <label className="block w-20">
            <span className="label">UF</span>
            <input
              className="input w-full"
              maxLength={2}
              value={form.ufNasc}
              onChange={(e) => setForm({ ...form, ufNasc: e.target.value })}
            />
          </label>
          <label className="block w-32">
            <span className="label">País</span>
            <select
              className="input w-full"
              value={form.paisNasc}
              onChange={(e) => setForm({ ...form, paisNasc: e.target.value })}
            >
              {PAISES.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="border-t border-sara-border pt-2 text-xs">
          <span className="label">Vincular contato (opcional, pra enviar depois)</span>
          {contato ? (
            <div className="mt-1 flex items-center justify-between gap-2">
              <span>
                <span className="font-medium">{contato.primeiroNome}</span> · {contato.telefone}
              </span>
              <button
                type="button"
                className="btn-ghost text-sara-danger"
                onClick={() => setContato(null)}
              >
                remover
              </button>
            </div>
          ) : picking ? (
            <div className="mt-1">
              <ContatoPicker
                onPick={(c) => {
                  setContato(c);
                  setPicking(false);
                }}
                onCancel={() => setPicking(false)}
              />
            </div>
          ) : (
            <button type="button" className="btn-ghost mt-1" onClick={() => setPicking(true)}>
              🔗 Buscar contato
            </button>
          )}
        </div>

        <button
          type="button"
          className="btn-primary w-full"
          disabled={!valido || criar.isPending}
          onClick={() => criar.mutate()}
        >
          {criar.isPending ? 'Gerando mapa… (~30-60s)' : 'Criar e gerar PDF'}
        </button>
        <p className="text-[11px] text-sara-text-muted">
          O PDF é gerado na hora (Swiss Ephemeris + IA). Depois fica disponível aqui pra baixar e
          enviar manualmente.
        </p>
      </div>
    </div>
  );
}
